import testimonials from '@data/testimonials.json';
import { ChevronLeft, ChevronRight, Pause, Play } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { EffectFade } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import 'swiper/css/effect-fade';

import { Button, Muted, Section, SectionHeader, Text } from '@/components/ui';
import { up } from '@/themes';

const AUTOPLAY_MS = 6000;

// Stable references: Swiper re-applies any param whose identity changes.
const SWIPER_MODULES = [EffectFade];
const FADE_EFFECT = { crossFade: true };

const Inner = styled.div`
  margin: 0 auto;
  max-width: 768px;
`;

const Header = styled.div`
  align-items: flex-end;
  display: flex;
  flex-wrap: wrap;
  gap: 20px 24px;
  justify-content: space-between;
`;

const Controls = styled.div`
  align-items: center;
  display: flex;
  gap: 4px;
`;

const RoundButton = styled(Button)`
  border-radius: 9999px;
`;

const Dots = styled.div`
  align-items: center;
  display: flex;
  padding: 0 4px;
`;

/* A 24px target around an 8px dot; the current one stretches into a pill. */
const Dot = styled.button`
  align-items: center;
  appearance: none;
  background: none;
  border: none;
  border-radius: 9999px;
  cursor: pointer;
  display: inline-flex;
  height: 24px;
  justify-content: center;
  min-width: 24px;
  padding: 0 4px;

  &::before {
    background-color: color-mix(in oklab, var(--foreground) 25%, transparent);
    border-radius: 9999px;
    content: '';
    height: 8px;
    transition:
      background-color 300ms,
      width 300ms;
    width: 8px;
  }

  &:hover::before {
    background-color: color-mix(in oklab, var(--foreground) 50%, transparent);
  }

  &[aria-current='true']::before {
    background-color: var(--primary);
    width: 24px;
  }

  &:focus-visible {
    outline: 2px solid var(--ring);
    outline-offset: 2px;
  }

  @media (prefers-reduced-motion: reduce) {
    &::before {
      transition: none;
    }
  }
`;

const Slides = styled.div`
  margin-top: 40px;

  /* Every slide takes the height of the tallest, so nothing moves on a change. */
  .swiper-slide {
    height: auto;
  }
`;

const Figure = styled.figure`
  border-left: 4px solid var(--brand-pink);
  margin: 0;
  padding-left: 20px;

  ${up.sm} {
    padding-left: 24px;
  }
`;

const Quote = styled.blockquote`
  margin: 0;
`;

const QuoteTitle = styled(Text)`
  font-weight: 600;
`;

const QuoteText = styled.p`
  color: var(--foreground);
  font-size: 22px;
  font-weight: 500;
  letter-spacing: -0.01em;
  line-height: 30px;
  margin: 8px 0 0;
  text-wrap: balance;

  ${up.sm} {
    font-size: 30px;
    line-height: 38px;
  }
`;

const Caption = styled.figcaption`
  align-items: center;
  display: flex;
  gap: 12px;
  margin-top: 24px;
`;

const Avatar = styled.span`
  align-items: center;
  background-image: var(--gradient-brand);
  border-radius: 9999px;
  color: var(--primary-foreground);
  display: inline-flex;
  flex-shrink: 0;
  font-size: 14px;
  font-weight: 600;
  height: 40px;
  justify-content: center;
  width: 40px;
`;

const Author = styled(Text)`
  font-weight: 600;
`;

/** "Gareth Emery" -> "GE". */
export const initialsFor = (name = '') =>
  name
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0].toUpperCase())
    .join('');

const Testimonial = ({ testimonial }) => (
  <Figure>
    <Quote>
      {testimonial.title && <QuoteTitle>{testimonial.title}</QuoteTitle>}
      <QuoteText>“{testimonial.message}”</QuoteText>
    </Quote>
    <Caption>
      <Avatar aria-hidden="true">{initialsFor(testimonial.author)}</Avatar>
      <div>
        <Author as="div">{testimonial.author}</Author>
        <Muted as="div">
          {[testimonial.author_title, testimonial.countryName].filter(Boolean).join(' · ')}
        </Muted>
      </div>
    </Caption>
  </Figure>
);

/**
 * Customer quotes, one at a time, cross-fading every few seconds.
 *
 * The slides are in the server HTML (the first one visible), and all take the
 * height of the tallest, so nothing shifts when Swiper starts. Rotation pauses
 * on hover and on keyboard focus, can be stopped with the pause button, and
 * never starts for visitors who prefer reduced motion. Arrow keys, the
 * previous / next buttons and the dots all change the quote.
 */
const Testimonials = () => {
  const t = useTranslations('testimonials');
  const swiperRef = useRef(null);
  const [active, setActive] = useState(0);
  const [autoplay, setAutoplay] = useState(true);
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);

  const playing = autoplay && !hovered && !focused;
  const total = testimonials.length;

  useEffect(() => {
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
      setAutoplay(false);
    }
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: `active` restarts the countdown after every change of quote
  useEffect(() => {
    if (!playing) return undefined;

    const timer = window.setInterval(() => {
      if (!document.hidden) swiperRef.current?.slideNext();
    }, AUTOPLAY_MS);

    return () => window.clearInterval(timer);
  }, [playing, active]);

  const handleKeyDown = (event) => {
    if (event.key === 'ArrowLeft') swiperRef.current?.slidePrev();
    if (event.key === 'ArrowRight') swiperRef.current?.slideNext();
  };

  const handleBlur = (event) => {
    if (!event.currentTarget.contains(event.relatedTarget)) setFocused(false);
  };

  if (total === 0) return null;

  return (
    <Section id="testimonials" $background="color-mix(in oklab, var(--muted) 50%, transparent)">
      {/* Rotation pauses while the pointer or keyboard focus is inside. */}
      <Inner
        onBlur={handleBlur}
        onFocus={() => setFocused(true)}
        onKeyDown={handleKeyDown}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        <Header>
          <SectionHeader eyebrow={t('eyebrow')} title={t('title')} />
          {total > 1 && (
            <Controls>
              <RoundButton
                $size="icon"
                $variant="outline"
                aria-label={t('previous')}
                onClick={() => swiperRef.current?.slidePrev()}
                type="button"
              >
                <ChevronLeft aria-hidden="true" size={20} strokeWidth={1.75} />
              </RoundButton>
              <Dots>
                {testimonials.map((testimonial, index) => (
                  <Dot
                    aria-current={index === active ? 'true' : undefined}
                    aria-label={t('goTo', { number: index + 1 })}
                    key={testimonial.id}
                    onClick={() => swiperRef.current?.slideTo(index)}
                    type="button"
                  />
                ))}
              </Dots>
              <RoundButton
                $size="icon"
                $variant="outline"
                aria-label={t('next')}
                onClick={() => swiperRef.current?.slideNext()}
                type="button"
              >
                <ChevronRight aria-hidden="true" size={20} strokeWidth={1.75} />
              </RoundButton>
              <RoundButton
                $size="icon"
                $variant="ghost"
                aria-label={autoplay ? t('pause') : t('play')}
                onClick={() => setAutoplay((value) => !value)}
                type="button"
              >
                {autoplay ? (
                  <Pause aria-hidden="true" size={20} strokeWidth={1.75} />
                ) : (
                  <Play aria-hidden="true" size={20} strokeWidth={1.75} />
                )}
              </RoundButton>
            </Controls>
          )}
        </Header>
        <Slides
          aria-label={t('title')}
          aria-live={playing ? 'off' : 'polite'}
          aria-roledescription="carousel"
          role="group"
        >
          <Swiper
            effect="fade"
            fadeEffect={FADE_EFFECT}
            modules={SWIPER_MODULES}
            onSlideChange={(swiper) => setActive(swiper.activeIndex)}
            onSwiper={(swiper) => {
              swiperRef.current = swiper;
            }}
            rewind
            simulateTouch={false}
            speed={700}
          >
            {testimonials.map((testimonial, index) => (
              <SwiperSlide
                aria-hidden={index === active ? undefined : 'true'}
                aria-label={t('position', { number: index + 1, total })}
                aria-roledescription="slide"
                key={testimonial.id}
                role="group"
              >
                <Testimonial testimonial={testimonial} />
              </SwiperSlide>
            ))}
          </Swiper>
        </Slides>
      </Inner>
    </Section>
  );
};

export default Testimonials;
